import React, { Component } from 'react'
import { connect } from 'react-redux'

import classNames from './DynamicLinks.css'
import { fetchDynamicLinks, saveDynamicLink, deleteDynamicLink } from '../redux/dynlinks'
import { fetchSearchOptions } from '../redux/search'
import { fetchKeywordGraph } from '../redux/keywords'
import LoadingSpinner from '../components/LoadingSpinner'
import ErrorMessage from '../components/ErrorMessage'
import EditDynamicLink from '../components/modals/EditDynamicLink'
import Icon from '../components/Icon'
import { isDate, isWeather } from '../utils/string'

class DynamicLinks extends Component {
  state = {
    creatingTable: false,
    createdTables: [],
    creating: false,
    editing: null,
    useDates: false,
    useWeather: false
  }
  componentDidMount () {
    const { data, fetchDynamicLinks, keywords, fetchSearchOptions } = this.props
    if (!data) fetchDynamicLinks()
    if (!keywords) fetchSearchOptions()
    if (this.props.shouldFetchGraph) this.props.fetchKeywordGraph()
  }
  saveDynamicLink = ({ type, condition, keywords }) => {
    const { saveDynamicLink } = this.props
    const { creating, editing } = this.state
    const id = creating ? undefined : editing.id
    const link = {
      type,
      condition,
      keywords: JSON.stringify(keywords.keywords)
    }
    return saveDynamicLink(link, id).then(this.closeEditForm)
  }
  closeEditForm = () => {
    this.setState({
      creating: false,
      editing: null
    })
  }
  onCreateTable = e => {
    e.preventDefault()
    this.setState({
      creatingTable: false,
      createdTables: [e.target.elements.name.value].concat(this.state.createdTables)
    })
  }
  getInitialValues = () => {
    const { creating, editing } = this.state
    if (editing) {
      return {
        ...editing,
        keywords: {
          search: '',
          keywords: JSON.parse(editing.keywords)
        }
      }
    }
    return {
      type: typeof creating === 'string' ? creating : ''
    }
  }
  sortTables = (a, b) => {
    if (a.length === 0 && b.length === 0) return 0
    if (a.length === 0 && b.length !== 0) return -1
    if (a.length !== 0 && b.length === 0) return 1
    const earliestA = Math.min(...a.map(d => new Date(d.created_at)))
    const earliestB = Math.min(...b.map(d => new Date(d.created_at)))
    return earliestA < earliestB ? 1 : -1
  }
  getKeywordName = id => {
    const { keywordMap } = this.props
    return keywordMap[id] ? keywordMap[id].name : 'Unknown Keyword'
  }
  getKeywordGroup = id => {
    const { keywordMap } = this.props
    return keywordMap[id] ? (keywordMap[id].top_parent_name || keywordMap[id].name) : 'Unknown'
  }
  renderDynamicLink = link => {
    const { deleteDynamicLink } = this.props
    const onEdit = () => this.setState({
      creatingTable: false,
      editing: link,
      useDates: !isWeather(link.condition),
      useWeather: isWeather(link.condition)
    })
    const onDelete = () => {
      if (window.confirm('Are you sure you want to delete this dynamic link?')) {
        deleteDynamicLink(link.id)
      }
    }
    return (
      <tr key={link.id}>
        <td className={classNames.condition}>
          {link.condition}
        </td>
        <td className={classNames.keywords}>
          {JSON.parse(link.keywords).map(this.getKeywordName).join(', ')}
        </td>
        <td className={classNames.tools}>
          <button
            onClick={onEdit}
            className={classNames.iconButton}>
            <Icon icon='edit' size={16} />
          </button>
          <button
            onClick={onDelete}
            className={classNames.iconButton}>
            <Icon icon='close' />
          </button>
        </td>
      </tr>
    )
  }
  renderTable = (type, links) => {
    const onClickAdd = () => {
      this.setState({
        creatingTable: false,
        creating: type,
        useDates: links.length === 0 || !isWeather(links[0].condition),
        useWeather: links.length !== 0 && isWeather(links[0].condition)
      })
    }
    return (
      <div key={type}>
        <h2>{type} Table</h2>
        <table><tbody>
          {links.map(this.renderDynamicLink)}
        </tbody></table>
        <button onClick={onClickAdd} className={classNames.addAnotherButton}>
          + Add condition
        </button>
      </div>
    )
  }
  render () {
    const { data, fetching, error, fetchDynamicLinks, keywordArray } = this.props

    if (error) {
      return (
        <ErrorMessage
          message='There was an error fetching dynamic links'
          onRetry={fetchDynamicLinks}
        />
      )
    }

    if (!data || !keywordArray || fetching) {
      return <LoadingSpinner />
    }

    const { creatingTable, createdTables, creating, editing, useDates, useWeather } = this.state

    const types = data.reduce((types, link) => {
      types[link.type] = types[link.type] || []
      types[link.type].push(link)
      return types
    }, {})

    for (let name of createdTables) {
      types[name] = types[name] || []
    }

    return (
      <div className={classNames.page}>
        <p className={classNames.intro}>
          You can create albums that change dynamically based on certain variables, like date and weather.
        </p>
        <p className={classNames.intro}>
          These dynamic tables are used by dynamic albums to set up all the conditions.
        </p>
        {!creatingTable &&
          <button className={classNames.createButton} onClick={() => this.setState({ creatingTable: true })}>
            Create New Dynamic Table
          </button>
        }
        {creatingTable &&
          <form onSubmit={this.onCreateTable} className={classNames.createForm}>
            <input
              type='text'
              name='name'
              placeholder='Enter a table name'
            />
            <input type='submit' value='Create' className={classNames.createSubmit} />
          </form>
        }
        {Object.keys(types)
          .sort((a, b) => this.sortTables(types[a], types[b]))
          .map(type => this.renderTable(type, types[type]))
        }
        {(editing || creating) &&
          <EditDynamicLink
            title={creating ? 'Add Condition to Match' : 'Edit Condition to Match'}
            onSubmit={this.saveDynamicLink}
            onCancel={this.closeEditForm}
            initialValues={this.getInitialValues()}
            types={Object.keys(types)}
            keywordArray={keywordArray}
            useDates={useDates}
            toggleUseDates={() => this.setState({ useDates: true, useWeather: false })}
            useWeather={useWeather}
            toggleUseWeather={() => this.setState({ useWeather: true, useDates: false })}
            getKeywordName={this.getKeywordName}
            getKeywordGroup={this.getKeywordGroup}
          />
        }
      </div>
    )
  }
}

const mapStateToProps = ({ dynlinks, search, keywords, entities }) => ({
  ...dynlinks,
  entities,
  fetching: dynlinks.fetching || keywords.graph.fetching,
  keywordArray: search.options.keywordArray,
  keywordMap: search.options.keywordMap,
  shouldFetchGraph: !keywords.graph.data
})

const mapDispatchToProps = {
  fetchDynamicLinks,
  saveDynamicLink,
  deleteDynamicLink,
  fetchSearchOptions,
  fetchKeywordGraph
}

export default connect(mapStateToProps, mapDispatchToProps)(DynamicLinks)



// WEBPACK FOOTER //
// ./src/pages/DynamicLinks.js